import { useState, useEffect } from 'react';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import {
  Users,
  FileText,
  Receipt,
  ScrollText,
  TrendingUp,
  TrendingDown,
  FileCheck,
  Loader2,
  MoreHorizontal,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign,
  AlertTriangle,
  Clock,
} from 'lucide-react';
import api from '../../../services/api';

interface DashboardStats {
  totais: {
    clientes: number;
    documentos: number;
    propostas: number;
    contratos: number;
    faturas: number;
    produtos: number;
    servicos: number;
    usuarios: number;
    documentosEsteMes: number;
    contratosAtivos: number;
  };
  financeiro: {
    mrr: number;
    contratosAtivos: number;
    faturasPendentes: number;
    faturasPendentesCount: number;
    faturasVencidas: number;
    faturasVencidasCount: number;
    faturamentoMesAtual: number;
    faturamentoMesPassado: number;
    variacaoFaturamento: number | null;
  };
  propostasPorStatus: Record<string, number>;
  faturasPorStatus: Record<string, number>;
  graficos: {
    documentosPorMes: { mes: string; quantidade: number }[];
    propostasPorMes: { mes: string; quantidade: number }[];
    faturamentoPorMes: { mes: string; valor: number }[];
  };
  ultimasAtividades: {
    documentos: any[];
    propostas: any[];
    faturas: any[];
  };
}

export function DashboardHome() {
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('outline');

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await api.get('/admin/dashboard/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  const chartData =
    stats?.graficos.documentosPorMes.map((item, index) => ({
      name: item.mes,
      documentos: item.quantidade,
      propostas: stats?.graficos.propostasPorMes[index]?.quantidade || 0,
      faturamento: stats?.graficos.faturamentoPorMes[index]?.valor || 0,
    })) || [];

  const varFat = stats?.financeiro.variacaoFaturamento;
  const varFatLabel = varFat !== null && varFat !== undefined
    ? `${varFat >= 0 ? '+' : ''}${varFat.toFixed(1)}%`
    : '—';
  const varFatTrend = varFat !== null && varFat !== undefined && varFat >= 0 ? 'up' : 'down';

  return (
    <div className="p-6 space-y-6 min-w-0 relative overflow-hidden">
      {/* Glow Effects - Light Mode */}
      <div className="glow-light-blue w-96 h-96 top-20 -left-20 animate-glow-pulse" />
      <div className="glow-light-cyan w-[500px] h-[500px] bottom-40 -right-32 animate-glow-drift" />
      
      {/* Glow Effects - Dark Mode */}
      <div className="glow-dark-blue w-[600px] h-[600px] top-10 -left-40 animate-glow-pulse" />
      <div className="glow-dark-cyan w-[500px] h-[500px] bottom-20 -right-20 animate-glow-drift" style={{ animationDelay: '3s' }} />
      <div className="glow-dark-blue w-96 h-96 top-1/2 left-1/3 animate-glow-pulse" style={{ animationDelay: '5s' }} />

      {/* Vertical lines pattern (paridade com o portal do cliente) */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border)/0.2)_1px,transparent_1px)] bg-[size:8rem_100%] pointer-events-none" style={{ maskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', maskComposite: 'intersect', WebkitMaskImage: 'radial-gradient(ellipse 80% 50% at 50% 0%, black 70%, transparent 110%), linear-gradient(to bottom, black 0%, black 85%, transparent 100%)', WebkitMaskComposite: 'source-in' }} />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 relative z-10">
        <KPICard
          title="MRR"
          value={formatCurrency(stats?.financeiro.mrr || 0)}
          change={`${stats?.financeiro.contratosAtivos || 0} contratos`}
          trend="up"
          subtitle="Receita Recorrente Mensal"
          description="Soma dos contratos ativos"
          icon={DollarSign}
          iconColor="text-green-500"
        />
        <KPICard
          title="Faturamento (mês)"
          value={formatCurrency(stats?.financeiro.faturamentoMesAtual || 0)}
          change={varFatLabel}
          trend={varFatTrend}
          subtitle={`Mês anterior: ${formatCurrency(stats?.financeiro.faturamentoMesPassado || 0)}`}
          description="Faturas pagas no mês corrente"
          icon={TrendingUp}
          iconColor="text-blue-500"
        />
        <KPICard
          title="A Receber"
          value={formatCurrency(stats?.financeiro.faturasPendentes || 0)}
          change={`${stats?.financeiro.faturasPendentesCount || 0} faturas`}
          trend="up"
          subtitle="Emitidas e enviadas"
          description="Total pendente de recebimento"
          icon={Clock}
          iconColor="text-yellow-500"
        />
        <KPICard
          title="Em Atraso"
          value={formatCurrency(stats?.financeiro.faturasVencidas || 0)}
          change={`${stats?.financeiro.faturasVencidasCount || 0} faturas`}
          trend={stats?.financeiro.faturasVencidasCount ? 'down' : 'up'}
          subtitle={stats?.financeiro.faturasVencidasCount ? 'Atenção: inadimplência' : 'Sem atrasos'}
          description="Faturas com vencimento expirado"
          icon={AlertTriangle}
          iconColor={stats?.financeiro.faturasVencidasCount ? 'text-red-500' : 'text-green-500'}
        />
      </div>

      {/* Chart Section */}
      <div className="card p-6 min-w-0 overflow-hidden relative z-10">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold">Total de Atividades</h2>
            <p className="text-sm text-muted-foreground">Últimos 6 meses</p>
          </div>
        </div>

        <div className="w-full">
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={chartData}>
            <defs>
              <linearGradient id="colorDocs" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--brand))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--brand))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorProps" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--chart-2))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--chart-2))" stopOpacity={0} />
              </linearGradient>
              <linearGradient id="colorFat" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="hsl(var(--chart-4))" stopOpacity={0.3} />
                <stop offset="95%" stopColor="hsl(var(--chart-4))" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" vertical={false} />
            <XAxis
              dataKey="name"
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            />
            <YAxis
              axisLine={false}
              tickLine={false}
              tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'hsl(var(--popover))',
                border: '1px solid hsl(var(--border))',
                borderRadius: '8px',
                color: 'hsl(var(--popover-foreground))',
              }}
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              formatter={((value: any, name: string) => [
                name === 'faturamento'
                  ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
                  : value,
                name === 'faturamento' ? 'Faturamento (R$)' : name === 'documentos' ? 'Documentos' : 'Propostas',
              ]) as any}
            />
            <Legend formatter={(v) => v === 'faturamento' ? 'Faturamento' : v === 'documentos' ? 'Documentos' : 'Propostas'} />
            <Area
              type="monotone"
              dataKey="documentos"
              stroke="hsl(var(--brand))"
              strokeWidth={2}
              fill="url(#colorDocs)"
            />
            <Area
              type="monotone"
              dataKey="propostas"
              stroke="hsl(var(--chart-2))"
              strokeWidth={2}
              fill="url(#colorProps)"
            />
            <Area
              type="monotone"
              dataKey="faturamento"
              stroke="hsl(var(--chart-4))"
              strokeWidth={2}
              fill="url(#colorFat)"
              yAxisId={0}
            />
          </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Tabs Section */}
      <div className="card relative z-10">
        <div className="flex items-center gap-2 p-4 border-b border-border">
          {[
            { id: 'outline', label: 'Resumo' },
            { id: 'performance', label: 'Performance', badge: stats?.totais.propostas },
            { id: 'personnel', label: 'Equipe', badge: stats?.totais.usuarios },
            { id: 'documents', label: 'Documentos' },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-2 text-sm rounded-md transition-colors flex items-center gap-2 border ${activeTab === tab.id
                ? 'bg-accent/10 border-accent/40 text-accent font-medium'
                : 'bg-transparent border-transparent text-muted-foreground hover:bg-accent/10 hover:text-accent'
                }`}
            >
              {tab.label}
              {tab.badge !== undefined && (
                <span className="bg-muted text-muted-foreground px-2 py-0.5 rounded text-xs">{tab.badge}</span>
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {activeTab === 'outline' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Clientes" value={stats?.totais.clientes || 0} />
              <StatCard
                icon={FileText}
                label="Documentos"
                value={stats?.totais.documentos || 0}
                subtitle={`${stats?.totais.documentosEsteMes || 0} este mês`}
              />
              <StatCard icon={FileCheck} label="Propostas" value={stats?.totais.propostas || 0} />
              <StatCard
                icon={ScrollText}
                label="Contratos Ativos"
                value={stats?.totais.contratosAtivos || 0}
              />
            </div>
          )}

          {activeTab === 'performance' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-4">
                Status das Propostas
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(stats?.propostasPorStatus || {}).map(([status, count]) => (
                  <div key={status} className="card-inner">
                    <p className="text-2xl font-bold">{count}</p>
                    <p className="text-sm text-muted-foreground capitalize">{status}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'personnel' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-4">Equipe</h3>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="card-inner">
                  <p className="text-2xl font-bold">{stats?.totais.usuarios || 0}</p>
                  <p className="text-sm text-muted-foreground">Usuários</p>
                </div>
                <div className="card-inner">
                  <p className="text-2xl font-bold">{stats?.totais.produtos || 0}</p>
                  <p className="text-sm text-muted-foreground">Produtos</p>
                </div>
                <div className="card-inner">
                  <p className="text-2xl font-bold">{stats?.totais.servicos || 0}</p>
                  <p className="text-sm text-muted-foreground">Serviços</p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'documents' && (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-muted-foreground mb-4">
                Documentos Recentes
              </h3>
              <div className="space-y-2">
                {stats?.ultimasAtividades.documentos.map((doc: any) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-3 bg-muted/50 rounded-lg hover:bg-muted transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-background rounded">
                        <FileText className="w-4 h-4 text-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-medium">{doc.name}</p>
                        <p className="text-xs text-muted-foreground">{doc.client?.name}</p>
                      </div>
                    </div>
                    <button className="p-1 hover:bg-accent rounded">
                      <MoreHorizontal className="w-4 h-4 text-muted-foreground" />
                    </button>
                  </div>
                ))}
                {(!stats?.ultimasAtividades.documentos ||
                  stats.ultimasAtividades.documentos.length === 0) && (
                    <p className="text-muted-foreground text-center py-8">Nenhum documento recente</p>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Propostas Recentes */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Propostas Recentes</h3>
            <button className="text-sm text-muted-foreground hover:text-foreground">
              Ver todas
            </button>
          </div>
          <div className="space-y-3">
            {stats?.ultimasAtividades.propostas.slice(0, 5).map((prop: any) => (
              <div
                key={prop.id}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-background rounded">
                    <FileCheck className="w-4 h-4 text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{prop.titulo}</p>
                    <p className="text-xs text-muted-foreground">{prop.client?.name}</p>
                  </div>
                </div>
                <StatusBadge status={prop.status} />
              </div>
            ))}
            {(!stats?.ultimasAtividades.propostas ||
              stats.ultimasAtividades.propostas.length === 0) && (
                <p className="text-muted-foreground text-center py-4">Nenhuma proposta recente</p>
              )}
          </div>
        </div>

        {/* Faturas Recentes */}
        <div className="card p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold">Faturas Recentes</h3>
            <button className="text-sm text-muted-foreground hover:text-foreground">
              Ver todas
            </button>
          </div>
          <div className="space-y-3">
            {stats?.ultimasAtividades.faturas.slice(0, 5).map((fat: any) => (
              <div
                key={fat.id}
                className="flex items-center justify-between p-3 bg-muted/50 rounded-lg"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-background rounded">
                    <Receipt className="w-4 h-4 text-foreground" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{fat.descricao}</p>
                    <p className="text-xs text-muted-foreground">{formatCurrency(fat.valor)}</p>
                  </div>
                </div>
                <StatusBadge status={fat.status} />
              </div>
            ))}
            {(!stats?.ultimasAtividades.faturas ||
              stats.ultimasAtividades.faturas.length === 0) && (
                <p className="text-muted-foreground text-center py-4">Nenhuma fatura recente</p>
              )}
          </div>
        </div>
      </div>
    </div>
  );
}

// KPI Card Component
function KPICard({
  title,
  value,
  change,
  trend,
  subtitle,
  description,
  icon: Icon,
  iconColor,
}: {
  title: string;
  value: string;
  change: string;
  trend: 'up' | 'down';
  subtitle: string;
  description: string;
  icon?: any;
  iconColor?: string;
}) {
  return (
    <div className="p-5 rounded-lg bg-card shadow-lg shadow-black/20">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={`w-4 h-4 ${iconColor || 'text-muted-foreground'}`} />}
          <span className="text-sm text-muted-foreground">{title}</span>
        </div>
        <span
          className={`flex items-center gap-1 text-xs ${trend === 'up' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
            }`}
        >
          {trend === 'up' ? (
            <ArrowUpRight className="w-3 h-3" />
          ) : (
            <ArrowDownRight className="w-3 h-3" />
          )}
          {change}
        </span>
      </div>
      <p className="text-2xl font-bold">{value}</p>
      <div className="flex items-center gap-1 text-sm text-muted-foreground">
        {trend === 'up' ? (
          <TrendingUp className="w-4 h-4" />
        ) : (
          <TrendingDown className="w-4 h-4" />
        )}
        <span>{subtitle}</span>
      </div>
      <p className="text-xs text-muted-foreground mt-1">{description}</p>
    </div>
  );
}

// Stat Card Component
function StatCard({
  icon: Icon,
  label,
  value,
  subtitle,
}: {
  icon: any;
  label: string;
  value: number;
  subtitle?: string;
}) {
  return (
    <div className="card-inner">
      <div className="flex items-center gap-3 mb-2">
        <div className="p-2 bg-background rounded">
          <Icon className="w-4 h-4 text-foreground" />
        </div>
        <span className="text-sm text-muted-foreground">{label}</span>
      </div>
      <p className="text-3xl font-bold">{value}</p>
      {subtitle && <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>}
    </div>
  );
}

// Status Badge Component
function StatusBadge({ status }: { status: string }) {
  const statusStyles: Record<string, string> = {
    rascunho: 'bg-muted text-muted-foreground',
    enviada: 'bg-blue-500/10 text-blue-600 dark:bg-blue-400/15 dark:text-blue-400',
    aprovada: 'bg-green-500/10 text-green-600 dark:bg-green-400/15 dark:text-green-400',
    rejeitada: 'bg-red-500/10 text-red-600 dark:bg-red-400/15 dark:text-red-400',
    pendente: 'bg-yellow-500/10 text-yellow-600 dark:bg-yellow-400/15 dark:text-yellow-400',
    paga: 'bg-green-500/10 text-green-600 dark:bg-green-400/15 dark:text-green-400',
    vencida: 'bg-red-500/10 text-red-600 dark:bg-red-400/15 dark:text-red-400',
    cancelada: 'bg-muted text-muted-foreground',
  };

  return (
    <span
      className={`px-2.5 py-0.5 text-xs rounded-full capitalize ${statusStyles[status] || 'bg-muted text-muted-foreground'
        }`}
    >
      {status}
    </span>
  );
}
